import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ArrowRight } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/company";
import { MEGA_MENU_ITEMS } from "@/data/products";
import { Button } from "@/components/ui/button";
import { onImgError } from "@/lib/images";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vortex-power-preview/artifacts/7ao8fprf_vortex-logo.png";

export function Header({ onOpenEnquiry }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setMegaOpen(false), 180);
  };
  const openMega = () => {
    cancelClose();
    setMegaOpen(true);
  };
  const closeMegaNow = () => {
    cancelClose();
    setMegaOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top strip */}
      <div className="hidden md:block bg-vortex-navy text-white/85 text-xs">
        <div className="container-wide flex items-center justify-between py-2">
          <div className="flex items-center gap-6">
            <span className="section-label !text-[10px] !py-0.5 !border-none !bg-transparent !text-vortex-blue">
              ISO 9001:2015 · CE Certified · Made in India
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href={`tel:${COMPANY.phone}`}
              className="flex items-center gap-2 hover:text-vortex-blue transition-colors"
              data-testid="topbar-phone-link"
            >
              <Phone className="w-3 h-3" strokeWidth={2} />
              {COMPANY.phone}
            </a>
            <span className="text-white/40">|</span>
            <span>{COMPANY.hours}</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl border-b border-vortex-border shadow-[0_4px_20px_rgba(0,65,90,0.06)]"
            : "bg-white border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="container-wide flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="site-logo-link"
            onMouseEnter={closeMegaNow}
          >
            <img
              src={LOGO_URL}
              alt="Vortex Power Solutions"
              className="h-10 md:h-12 w-auto"
            />
            <div className="hidden sm:block leading-tight border-l border-vortex-border pl-3">
              <div className="text-[10px] tracking-[0.25em] font-bold text-vortex-navy uppercase">
                Power
              </div>
              <div className="text-[10px] tracking-[0.25em] font-bold text-vortex-slate uppercase">
                Solutions
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.mega ? (
                <button
                  key={link.to}
                  onMouseEnter={openMega}
                  onMouseLeave={scheduleClose}
                  onClick={() => setMegaOpen((v) => !v)}
                  className={`px-4 py-2.5 flex items-center gap-1 text-sm font-semibold rounded-full transition-colors ${
                    location.pathname.startsWith("/products")
                      ? "text-vortex-navy bg-vortex-alt"
                      : "text-vortex-slate hover:text-vortex-navy"
                  }`}
                  data-testid="nav-products-trigger"
                >
                  {link.label}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      megaOpen ? "rotate-180" : ""
                    }`}
                    strokeWidth={2}
                  />
                </button>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onMouseEnter={closeMegaNow}
                  className={({ isActive }) =>
                    `px-4 py-2.5 text-sm font-semibold rounded-full transition-colors ${
                      isActive
                        ? "text-vortex-navy bg-vortex-alt"
                        : "text-vortex-slate hover:text-vortex-navy"
                    }`
                  }
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </NavLink>
              ),
            )}
          </nav>

          {/* Right CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phone}`}
              className="flex items-center gap-2 text-sm font-semibold text-vortex-navy hover:text-vortex-blue transition-colors"
              data-testid="header-call-link"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              {COMPANY.phone}
            </a>
            <Button
              onClick={onOpenEnquiry}
              className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white px-6 h-11 font-semibold shadow-lg hover:shadow-orange-500/25 transition-all"
              data-testid="header-quote-btn"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 -mr-2 text-vortex-navy"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mega menu */}
        {megaOpen && (
          <div
          className="hidden lg:block absolute left-0 right-0 top-full bg-[#ffffff] opacity-100 border-t border-vortex-border shadow-[0_20px_40px_-15px_rgba(0,65,90,0.15)]"
            onMouseEnter={openMega}
            onMouseLeave={scheduleClose}
            data-testid="mega-menu"
          >
            <div className="container-wide py-10">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <div className="section-label mb-3">Our Products</div>
                  <h3 className="font-heading font-bold text-2xl text-vortex-navy leading-tight">
                    Industrial-grade power equipment.
                  </h3>
                </div>
                <Link
                  to="/products"
                  className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-vortex-navy hover:text-vortex-blue transition-colors"
                  data-testid="mega-menu-view-all"
                >
                  View all products
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
              </div>

              {/* 4×3 grid of 12 product tiles — 120x90 image on top, name below */}
              <div className="grid grid-cols-4 gap-4">
                {MEGA_MENU_ITEMS.map((item, i) => (
                  <Link
                    key={`${item.slug}-${i}`}
                    to={`/products/${item.slug}`}
                    className="group flex flex-col items-center text-center p-3 rounded-xl border border-vortex-border bg-white hover:border-vortex-blue hover:bg-vortex-alt hover:-translate-y-0.5 transition-all"
                    data-testid={`mega-menu-item-${i}`}
                  >
                    <div className="w-[120px] h-[90px] flex-shrink-0 rounded-lg overflow-hidden bg-vortex-alt border border-vortex-border mb-3">
                      <img
                        src={item.image}
                        onError={onImgError}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="font-heading font-semibold text-base text-vortex-navy group-hover:text-vortex-blue leading-tight transition-colors">
                      {item.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-vortex-border bg-white">
            <div className="container-wide py-6 space-y-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl font-semibold text-base transition-colors ${
                      isActive
                        ? "bg-vortex-alt text-vortex-navy"
                        : "text-vortex-slate hover:bg-vortex-alt"
                    }`
                  }
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="pt-4 mt-4 border-t border-vortex-border space-y-2">
                <a
                  href={`tel:${COMPANY.phone}`}
                  className="flex items-center gap-2 px-4 py-3 text-vortex-navy font-semibold"
                >
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
                <Button
                  onClick={onOpenEnquiry}
                  className="w-full rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 font-semibold"
                  data-testid="mobile-quote-btn"
                >
                  Get Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
